import React, { useState } from 'react';
import { AppShell } from '@/components';
import { MarketingSection, SectionHeader, GlassPanel } from '@/components/marketing';
import { functionsService } from '@/services/functions';
import { authService } from '@/services/auth';
import apiService from '@/services/api';

const initialCode = `def handler(name: str = "World"):
    return f"Hello, {name}!"`;

const inputClass =
  'mt-2 w-full rounded-xl border border-white/15 bg-slate-900/60 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-emerald-400 focus:outline-none';
const textareaClass =
  'mt-2 w-full rounded-xl border border-white/15 bg-slate-900/60 px-4 py-3 text-sm font-mono text-emerald-100 leading-relaxed placeholder:text-slate-500 focus:border-emerald-400 focus:outline-none';
const buttonBase =
  'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed';

const Studio: React.FC = () => {
  const [functionName, setFunctionName] = useState('hello_world');
  const [signature, setSignature] = useState('handler(name: str = "World")');
  const [source, setSource] = useState(initialCode);
  const [description, setDescription] = useState('Demo function created from the Studio');
  const [rufid, setRufid] = useState<string | null>(null);
  const [rufidShort, setRufidShort] = useState<string | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [devKey, setDevKey] = useState<string | null>(null);
  const [inputJson, setInputJson] = useState<string>('{}');

  const push = (line: string) => setLog((prev) => [line, ...prev].slice(0, 80));

  const createDevKey = async () => {
    if (!adminKey) {
      push('Provide ADMIN_API_KEY to mint a dev API key.');
      return;
    }
    setBusy(true);
    push('Creating dev API key...');
    const resp = await authService.createApiKey(
      { name: 'studio-demo', scopes: ['functions:*', 'read', 'write'], expires_days: 7 },
      adminKey,
    );
    if (resp.status === 'success' && resp.data && resp.data.key) {
      setDevKey(resp.data.key);
      apiService.setAuthToken(resp.data.key);
      try {
        localStorage.setItem('relay_auth_token', resp.data.key);
      } catch (error) {
        console.warn('Failed to persist dev key', error);
      }
      push('Dev key created and applied to this session.');
    } else {
      push(`Failed to create dev key: ${resp.error?.message}`);
    }
    setBusy(false);
  };

  const createFn = async () => {
    setBusy(true);
    push('Creating function...');
    const resp = await functionsService.create({
      function_name: functionName,
      signature,
      source_code: source,
      description,
      tags: ['studio', 'demo'],
    });
    if (resp.status === 'success' && resp.data) {
      setRufid(resp.data.rufid);
      setRufidShort(resp.data.rufid_short);
      push(`Created rufid=${resp.data.rufid} short=${resp.data.rufid_short}`);
    } else {
      push(`Create failed: ${resp.error?.message}`);
    }
    setBusy(false);
  };

  const resolveFn = async () => {
    if (!rufid && !rufidShort) return;
    setBusy(true);
    const id = rufid ?? (rufidShort as string);
    push(`Resolving ${id} ...`);
    const resp = await functionsService.resolve(id);
    if (resp.status === 'success') {
      push(`Resolve: ${JSON.stringify(resp.data)}`);
    } else {
      push(`Resolve failed: ${resp.error?.message}`);
    }
    setBusy(false);
  };

  const executeFn = async () => {
    if (!rufidShort) return;
    setBusy(true);
    push(`Executing ${rufidShort} ...`);
    let payload: any = undefined;
    try {
      payload = JSON.parse(inputJson || '{}');
    } catch {
      push('Invalid JSON payload; sending empty body.');
    }
    const resp = await functionsService.execute(rufidShort, payload);
    if (resp.status === 'success') {
      push(`Execute: ${JSON.stringify(resp.data)}`);
    } else {
      push(`Execute failed: ${resp.error?.message}`);
    }
    setBusy(false);
  };

  const deleteFn = async () => {
    if (!rufid) return;
    setBusy(true);
    push(`Deleting ${rufid} ...`);
    const resp = await functionsService.remove(rufid);
    if (resp.status === 'success') {
      push('Deleted.');
      setRufid(null);
      setRufidShort(null);
    } else {
      push(`Delete failed: ${resp.error?.message}`);
    }
    setBusy(false);
  };

  return (
    <AppShell>
      <div className="bg-slate-950 text-slate-50">
        <MarketingSection theme="gradient" collapsePadding>
          <div className="grid gap-12 py-16 lg:grid-cols-[1.2fr_0.8fr] lg:py-24">
            <div>
              <SectionHeader
                overline="Relay Studio"
                title="Design, test, and ship shared serverless functions with confidence"
                description="Transform notebooks and scripts into production-ready functions. Studio keeps policy, telemetry, and monetization context beside the code so every deploy stays on rails."
              />
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href="/dashboard"
                  className="inline-flex items-center justify-center rounded-lg bg-emerald-500 px-6 py-3 text-base font-semibold text-slate-950 shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-400"
                >
                  Launch the console
                </a>
                <a
                  href="/pricing"
                  className="inline-flex items-center justify-center rounded-lg border border-white/20 px-6 py-3 text-base font-semibold text-slate-200 transition hover:border-white hover:text-white"
                >
                  View plans
                </a>
              </div>
            </div>
            <GlassPanel padding="lg" className="space-y-6">
              <h3 className="text-lg font-semibold text-white">Why teams use Studio</h3>
              <ul className="space-y-3 text-sm text-slate-300">
                <li>• Write and test functions with real-time policy validation.</li>
                <li>• Promote across environments with branch-aware RUFIDs.</li>
                <li>• Inspect telemetry, logs, and budgets in the same workspace.</li>
              </ul>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200">
                <div className="font-semibold text-white">Instant preview</div>
                <p className="mt-2">
                  Every saved iteration compiles, runs unit hooks, and validates policy budgets automatically—Studio will flag regressions before they reach production.
                </p>
              </div>
            </GlassPanel>
          </div>
        </MarketingSection>

        <MarketingSection theme="dark">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <GlassPanel padding="lg" className="space-y-5">
              <div>
                <h2 className="text-xl font-semibold text-white">Mint a dev key</h2>
                <p className="text-sm text-slate-300">
                  Use your local <code className="font-mono text-emerald-200">ADMIN_API_KEY</code> once to mint a 7-day demo key. We store it locally for your session.
                </p>
                <div className="mt-4 flex flex-col gap-3 md:flex-row">
                  <input
                    type="password"
                    placeholder="Paste ADMIN_API_KEY"
                    value={adminKey}
                    onChange={(e) => setAdminKey(e.target.value)}
                    className={inputClass}
                  />
                  <button
                    onClick={createDevKey}
                    disabled={busy}
                    className={`${buttonBase} bg-slate-900 border border-white/20 text-slate-100 hover:border-white/40`}
                  >
                    Create dev key
                  </button>
                </div>
                {devKey && (
                  <div className="mt-3 rounded-lg bg-slate-900/60 p-3 text-xs text-slate-300">
                    <div className="font-semibold text-white">Session key</div>
                    <div className="mt-1 font-mono break-all">{devKey}</div>
                  </div>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-slate-200">Function name</label>
                  <input
                    value={functionName}
                    onChange={(e) => setFunctionName(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-200">Signature</label>
                  <input
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-200">Description</label>
                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-200">Source code (Python)</label>
                <textarea
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  rows={12}
                  className={textareaClass}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-200">Execution input (JSON)</label>
                <textarea
                  value={inputJson}
                  onChange={(e) => setInputJson(e.target.value)}
                  rows={6}
                  className={textareaClass}
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={createFn}
                  disabled={busy}
                  className={`${buttonBase} bg-emerald-500 text-slate-950 hover:bg-emerald-400`}
                >
                  Create
                </button>
                <button
                  onClick={resolveFn}
                  disabled={busy || (!rufid && !rufidShort)}
                  className={`${buttonBase} border border-white/20 text-slate-100 hover:border-white/40`}
                >
                  Resolve
                </button>
                <button
                  onClick={executeFn}
                  disabled={busy || !rufidShort}
                  className={`${buttonBase} border border-white/20 text-slate-100 hover:border-white/40`}
                >
                  Execute
                </button>
                <button
                  onClick={deleteFn}
                  disabled={busy || !rufid}
                  className={`${buttonBase} bg-rose-500/90 text-white hover:bg-rose-400/90`}
                >
                  Delete
                </button>
              </div>

              {(rufid || rufidShort) && (
                <GlassPanel padding="sm" className="bg-white/5">
                  {rufid && (
                    <div className="text-xs text-slate-200">
                      <span className="font-semibold text-white">RUFID:</span> {rufid}
                    </div>
                  )}
                  {rufidShort && (
                    <div className="text-xs text-slate-200">
                      <span className="font-semibold text-white">Short ID:</span> {rufidShort}
                    </div>
                  )}
                </GlassPanel>
              )}
            </GlassPanel>

            <GlassPanel padding="lg" className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-white">Activity</h2>
                  <p className="text-xs text-slate-400">Latest Studio actions appear here. We keep the last 80 entries.</p>
                </div>
                <button
                  className="text-xs text-slate-400 hover:text-slate-200"
                  onClick={() => setLog([])}
                >
                  Clear
                </button>
              </div>
              <div className="h-[420px] overflow-auto rounded-xl border border-white/10 bg-slate-950/60 p-4 text-xs font-mono text-slate-200">
                {log.length === 0 ? (
                  <div className="text-slate-500">No activity yet. Create a function to get started.</div>
                ) : (
                  <ul className="space-y-2">
                    {log.map((line, index) => (
                      <li key={`${line}-${index}`}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
            </GlassPanel>
          </div>
        </MarketingSection>

        <MarketingSection theme="deep" collapsePadding>
          <GlassPanel padding="xl" className="mx-auto max-w-5xl text-center space-y-6">
            <h2 className="text-3xl font-semibold text-white sm:text-4xl">From prototype to production without leaving Studio</h2>
            <p className="text-base text-slate-300">
              Studio connects code, policy, telemetry, and monetization workflows so teams can accelerate launches without sacrificing control. Ready to ship your next function?
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="/dashboard"
                className="inline-flex items-center justify-center rounded-lg bg-emerald-500 px-6 py-3 text-base font-semibold text-slate-950 transition hover:bg-emerald-400"
              >
                Open Studio in console
              </a>
              <a
                href="/resources"
                className="inline-flex items-center justify-center rounded-lg border border-white/20 px-6 py-3 text-base font-semibold text-slate-200 transition hover:border-white hover:text-white"
              >
                Browse resources
              </a>
            </div>
          </GlassPanel>
        </MarketingSection>
      </div>
    </AppShell>
  );
};

export default Studio;
